import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../ui-components/ui/card';
import { Button } from '../../../ui-components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../ui-components/ui/select';
import { Input } from '../../../ui-components/ui/input';
import { Label } from '../../../ui-components/ui/label';
import { Textarea } from '../../../ui-components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../../ui-components/ui/dialog';
import { toast } from 'sonner';
import { Mail, Send, FileText, Eye, Edit, Clock } from 'lucide-react';
import useApiRequest from '../../../lib/hooks/use-request';
import { useUser } from '../../../lib/hooks/use-user';

interface EmailTemplate {
  _id?: string;
  templateId: string;
  name: string;
  subject: string;
  htmlContent: string;
  textContent: string;
  category: string;
  variables?: string[];
  isActive: boolean;
}

interface EmailTemplatesProps {
  dealId: string;
  contactEmail?: string;
  contactName?: string;
}

const EmailTemplates: React.FC<EmailTemplatesProps> = ({ dealId, contactEmail, contactName }) => {
  const { user } = useUser();
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<EmailTemplate | null>(null);
  const [showPreview, setShowPreview] = useState(false);
  const [showSendDialog, setShowSendDialog] = useState(false);
  const [emailData, setEmailData] = useState({
    to: contactEmail || '',
    subject: '',
    content: ''
  });
  const [sentEmails, setSentEmails] = useState<any[]>([]);
  
  const { apiRequest } = useApiRequest();

  // Default templates for demonstration
  const defaultTemplates: EmailTemplate[] = [
    {
      templateId: 'not_reached_20min',
      name: 'Nicht erreicht - 20 Minuten Follow-up',
      subject: 'Ihr Interesse an {{productName}} - Blockalarm',
      htmlContent: `<h2>Guten Tag {{recipientName}},</h2>
<p>leider konnte ich Sie telefonisch nicht erreichen.</p>
<p>Sie hatten Interesse an <strong>{{productName}}</strong> bekundet. Gerne würde ich Ihnen die Vorteile unserer Lösung persönlich vorstellen.</p>
<p>Wann darf ich Sie für ein kurzes Gespräch zurückrufen?</p>
<ul>
  <li>Morgen zwischen 10:00 und 12:00 Uhr</li>
  <li>Morgen zwischen 14:00 und 16:00 Uhr</li>
  <li>Oder teilen Sie mir einfach Ihren Wunschtermin mit</li>
</ul>
<p>Beste Grüße<br>
{{senderName}}<br>
{{senderTitle}}<br>
{{senderPhone}}</p>`,
      textContent: 'Email text content...',
      category: 'follow_up',
      variables: ['recipientName', 'productName', 'senderName', 'senderTitle', 'senderPhone'],
      isActive: true
    },
    {
      templateId: 'appointment_confirmation',
      name: 'Terminbestätigung',
      subject: 'Terminbestätigung - {{appointmentDate}} um {{appointmentTime}} Uhr',
      htmlContent: `<h2>Sehr geehrte/r {{recipientName}},</h2>
<p>vielen Dank für Ihr Interesse. Hiermit bestätige ich unseren Termin:</p>
<div style="background: #f5f5f5; padding: 20px; border-radius: 8px; margin: 20px 0;">
  <p><strong>Datum:</strong> {{appointmentDate}}</p>
  <p><strong>Uhrzeit:</strong> {{appointmentTime}} Uhr</p>
  <p><strong>Ort/Medium:</strong> {{appointmentLocation}}</p>
  <p><strong>Thema:</strong> {{appointmentSubject}}</p>
</div>`,
      textContent: 'Email text content...',
      category: 'appointment',
      variables: ['recipientName', 'appointmentDate', 'appointmentTime', 'appointmentLocation', 'appointmentSubject'],
      isActive: true
    },
    {
      templateId: 'offer_followup',
      name: 'Angebot Follow-up',
      subject: 'Ihr Angebot für {{productName}} - Haben Sie noch Fragen?',
      htmlContent: `<h2>Sehr geehrte/r {{recipientName}},</h2>
<p>vor einigen Tagen habe ich Ihnen unser Angebot für <strong>{{productName}}</strong> zugesandt.</p>
<p>Ich wollte mich erkundigen, ob Sie bereits Gelegenheit hatten, das Angebot zu prüfen und ob Sie noch Fragen haben?</p>`,
      textContent: 'Email text content...',
      category: 'offer',
      variables: ['recipientName', 'productName'],
      isActive: true
    }
  ];

  useEffect(() => {
    fetchTemplates();
    loadSentEmails();
  }, []);

  const fetchTemplates = async () => {
    try {
      const response = await apiRequest<EmailTemplate[]>('email-templates', 'GET');
      if (response.data && response.data.length > 0) {
        setTemplates(response.data);
      } else {
        setTemplates(defaultTemplates);
      }
    } catch (error) {
      setTemplates(defaultTemplates);
    }
  };

  const loadSentEmails = () => {
    const stored = localStorage.getItem(`deal_emails_${dealId}`);
    if (stored) {
      setSentEmails(JSON.parse(stored));
    }
  };

  const handleTemplateSelect = (templateId: string) => {
    const template = templates.find(t => t.templateId === templateId);
    if (template) {
      setSelectedTemplate(template);
      // Replace variables with actual values
      let processedSubject = template.subject;
      let processedContent = template.htmlContent;
      
      // Basic variable replacements
      const replacements: Record<string, string> = {
        recipientName: contactName || 'Kunde',
        productName: 'Blockalarm System',
        senderName: user ? `${user.firstName} ${user.lastName}` : 'Unbekannter Benutzer',
        senderTitle: 'Vertriebsmitarbeiter',
        senderPhone: '+49 123 456789',
        appointmentDate: new Date().toLocaleDateString('de-DE'),
        appointmentTime: '14:00',
        appointmentLocation: 'Telefonisch',
        appointmentSubject: 'Blockalarm Präsentation'
      };
      
      Object.entries(replacements).forEach(([key, value]) => {
        processedSubject = processedSubject.replace(new RegExp(`{{${key}}}`, 'g'), value);
        processedContent = processedContent.replace(new RegExp(`{{${key}}}`, 'g'), value);
      });
      
      setEmailData({
        to: contactEmail || '',
        subject: processedSubject,
        content: processedContent
      });
    }
  };

  const handleSendEmail = async () => {
    if (!emailData.to || !emailData.subject) {
      toast.error('Bitte füllen Sie alle Pflichtfelder aus');
      return;
    }

    // Save to localStorage
    const newEmail = {
      _id: `email_${Date.now()}`,
      dealId,
      templateId: selectedTemplate?.templateId,
      templateName: selectedTemplate?.name,
      to: emailData.to,
      subject: emailData.subject,
      content: emailData.content,
      sentAt: new Date().toISOString(),
      sentBy: user ? `${user.firstName} ${user.lastName}` : 'Unbekannter Benutzer'
    };

    const updatedEmails = [...sentEmails, newEmail];
    localStorage.setItem(`deal_emails_${dealId}`, JSON.stringify(updatedEmails));
    setSentEmails(updatedEmails);

    // Add to deal history
    const historyEntry = {
      _id: `history_email_${Date.now()}`,
      dealId,
      belongsToUserId: '17',
      historyType: 'EMAIL' as any,
      historyNoteContent: {
        _id: `note_${Date.now()}`,
        description: `E-Mail gesendet an ${emailData.to}\nBetreff: ${emailData.subject}\nVorlage: ${selectedTemplate?.name || 'Keine Vorlage'}`
      },
      createdAt: new Date().toISOString(),
      belongsToUserFullName: user ? `${user.firstName} ${user.lastName}` : 'Unbekannter Benutzer'
    };

    const existingHistory = JSON.parse(localStorage.getItem(`deal_history_${dealId}`) || '[]');
    localStorage.setItem(`deal_history_${dealId}`, JSON.stringify([historyEntry, ...existingHistory]));

    toast.success('E-Mail erfolgreich gesendet');
    setShowSendDialog(false);
    setSelectedTemplate(null);
    setEmailData({ to: contactEmail || '', subject: '', content: '' });
  };

  return (
    <div className="space-y-4">
      {/* Email Templates Selection */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            E-Mail Vorlagen
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div>
              <Label>Vorlage auswählen</Label>
              <Select onValueChange={handleTemplateSelect}>
                <SelectTrigger>
                  <SelectValue placeholder="Wählen Sie eine E-Mail Vorlage" />
                </SelectTrigger>
                <SelectContent>
                  {templates.map((template) => (
                    <SelectItem key={template.templateId} value={template.templateId}>
                      {template.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {selectedTemplate && (
              <div className="space-y-4 border-t pt-4">
                <div>
                  <Label>Empfänger</Label>
                  <Input
                    type="email"
                    value={emailData.to}
                    onChange={(e) => setEmailData({ ...emailData, to: e.target.value })}
                    placeholder="email@example.com"
                  />
                </div>

                <div>
                  <Label>Betreff</Label>
                  <Input
                    value={emailData.subject}
                    onChange={(e) => setEmailData({ ...emailData, subject: e.target.value })}
                  />
                </div>

                <div>
                  <Label>Inhalt</Label>
                  <div className="border rounded-md p-4 bg-gray-50 max-h-64 overflow-y-auto">
                    <div dangerouslySetInnerHTML={{ __html: emailData.content }} />
                  </div>
                </div>

                <div className="flex gap-2">
                  <Button onClick={() => setShowPreview(true)} variant="outline">
                    <Eye className="h-4 w-4 mr-2" />
                    Vorschau
                  </Button>
                  <Button onClick={() => setShowSendDialog(true)}>
                    <Send className="h-4 w-4 mr-2" />
                    E-Mail senden
                  </Button>
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Sent Emails History */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="h-5 w-5" />
            Gesendete E-Mails
          </CardTitle>
        </CardHeader>
        <CardContent>
          {sentEmails.length === 0 ? (
            <p className="text-gray-500 text-center py-4">Noch keine E-Mails gesendet</p>
          ) : (
            <div className="space-y-2">
              {sentEmails.map((email) => (
                <div key={email._id} className="border rounded-lg p-3 bg-gray-50">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <p className="font-medium">{email.subject}</p>
                      <p className="text-sm text-gray-600">An: {email.to}</p>
                      {email.templateName && (
                        <p className="text-sm text-gray-500">Vorlage: {email.templateName}</p>
                      )}
                    </div>
                    <div className="text-right text-sm text-gray-500">
                      <p>{new Date(email.sentAt).toLocaleDateString('de-DE')}</p>
                      <p>{new Date(email.sentAt).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Preview Dialog */}
      <Dialog open={showPreview} onOpenChange={setShowPreview}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>E-Mail Vorschau</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>An:</Label>
              <p className="text-sm">{emailData.to}</p>
            </div>
            <div>
              <Label>Betreff:</Label>
              <p className="text-sm">{emailData.subject}</p>
            </div>
            <div>
              <Label>Inhalt:</Label>
              <div className="border rounded-md p-4 bg-white">
                <div dangerouslySetInnerHTML={{ __html: emailData.content }} />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPreview(false)}>
              Schließen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Send Confirmation Dialog */}
      <Dialog open={showSendDialog} onOpenChange={setShowSendDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>E-Mail senden bestätigen</DialogTitle>
          </DialogHeader>
          <p>Möchten Sie diese E-Mail wirklich senden?</p>
          <div className="mt-4 p-3 bg-gray-50 rounded-md">
            <p className="text-sm"><strong>An:</strong> {emailData.to}</p>
            <p className="text-sm"><strong>Betreff:</strong> {emailData.subject}</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSendDialog(false)}>
              Abbrechen
            </Button>
            <Button onClick={handleSendEmail}>
              <Send className="h-4 w-4 mr-2" />
              Senden
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EmailTemplates;
